import React, { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useI18n } from "../lib/i18n";
import LangSwitcher from "../components/LangSwitcher";
import { Sparkles, Lock, Loader2, CheckCircle2, XCircle, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "../components/ui/sonner";
import { api } from "../lib/api";

export default function ResetPassword() {
  const { lang } = useI18n();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [pwd2, setPwd2] = useState("");
  const [totp, setTotp] = useState("");
  const [needs2fa, setNeeds2fa] = useState(false);
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [state, setState] = useState(token ? "form" : "missing");
  // states: form | success | missing | failed

  const tr = (ca, es) => (lang === "es" ? es : ca);

  useEffect(() => {
    if (!token) setState("missing");
  }, [token]);

  const valid = pwd.length >= 8 && pwd === pwd2 && (!needs2fa || totp.length >= 6);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!valid) {
      toast.error(pwd.length < 8
        ? tr("Mínim 8 caràcters", "Mínimo 8 caracteres")
        : pwd !== pwd2
        ? tr("Les contrasenyes no coincideixen", "Las contraseñas no coinciden")
        : tr("Introdueix el codi 2FA", "Introduce el código 2FA"));
      return;
    }
    setBusy(true);
    try {
      const payload = { token, new_password: pwd };
      if (needs2fa && totp) payload.totp_code = totp;
      await api.post("/auth/reset-password", payload);
      setState("success");
      toast.success(tr("Contrasenya actualitzada", "Contraseña actualizada"));
      setTimeout(() => nav("/login"), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      // Structured error for 2FA flow
      const code = (detail && typeof detail === "object") ? detail.code : null;
      const msg = (detail && typeof detail === "object") ? detail.message
                  : typeof detail === "string" ? detail
                  : tr("Error inesperat", "Error inesperado");
      if (code === "totp_required") {
        setNeeds2fa(true);
        toast.info(tr(
          "Aquest compte té 2FA. Introdueix el codi de l'autenticador.",
          "Esta cuenta tiene 2FA. Introduce el código del autenticador."));
      } else if (code === "totp_invalid") {
        toast.error(tr("Codi 2FA invàlid", "Código 2FA inválido"));
      } else {
        toast.error(msg);
        if (typeof detail === "string" &&
            /caducat|caducad|utilitzat|utiliz|invàlid|inválid|invalid/i.test(detail)) {
          setState("failed");
        }
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden p-5">
      <div className="absolute inset-0 grid-bg pointer-events-none" />
      <div className="absolute top-5 left-5">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center">
            <Sparkles size={16} className="text-white" />
          </div>
          <span className="font-display font-bold">HOSYS<span className="text-blue-400">.AI</span></span>
        </Link>
      </div>
      <div className="absolute top-5 right-5"><LangSwitcher /></div>

      <div className="relative w-full max-w-md card-surface p-8 sm:p-10"
           data-testid="reset-password-card">

        {state === "missing" && (
          <div className="text-center space-y-4" data-testid="reset-missing-state">
            <div className="w-14 h-14 rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mx-auto">
              <XCircle className="w-7 h-7 text-rose-300" />
            </div>
            <h1 className="font-display text-2xl font-bold">
              {tr("Enllaç invàlid", "Enlace inválido")}
            </h1>
            <p className="text-sm text-slate-400">
              {tr(
                "No s'ha proporcionat cap token. Torna a sol·licitar el restabliment.",
                "No se ha proporcionado token. Vuelve a solicitar el restablecimiento."
              )}
            </p>
            <Link to="/forgot-password"
                  className="inline-block text-sm text-blue-400 hover:text-blue-300"
                  data-testid="reset-back-to-forgot">
              {tr("Demanar de nou", "Pedir de nuevo")}
            </Link>
          </div>
        )}

        {state === "failed" && (
          <div className="text-center space-y-4" data-testid="reset-failed-state">
            <div className="w-14 h-14 rounded-full bg-rose-500/15 border border-rose-500/40 flex items-center justify-center mx-auto">
              <XCircle className="w-7 h-7 text-rose-300" />
            </div>
            <h1 className="font-display text-2xl font-bold">
              {tr("Enllaç caducat o usat", "Enlace caducado o usado")}
            </h1>
            <p className="text-sm text-slate-400">
              {tr(
                "Aquest enllaç ja no és vàlid. Demana'n un de nou per restablir la contrasenya.",
                "Este enlace ya no es válido. Solicita uno nuevo para restablecer la contraseña."
              )}
            </p>
            <Link to="/forgot-password"
                  className="inline-block text-sm text-blue-400 hover:text-blue-300"
                  data-testid="reset-request-new">
              {tr("Sol·licitar un nou enllaç", "Solicitar un nuevo enlace")}
            </Link>
          </div>
        )}

        {state === "success" && (
          <div className="text-center space-y-4" data-testid="reset-success-state">
            <div className="w-14 h-14 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-7 h-7 text-emerald-300" />
            </div>
            <h1 className="font-display text-2xl font-bold">
              {tr("Contrasenya actualitzada", "Contraseña actualizada")}
            </h1>
            <p className="text-sm text-slate-400">
              {tr(
                "Ja pots iniciar sessió amb la nova contrasenya. Redirigint...",
                "Ya puedes iniciar sesión con la nueva contraseña. Redirigiendo..."
              )}
            </p>
          </div>
        )}

        {state === "form" && (
          <>
            <h1 className="font-display text-3xl font-bold tracking-tighter mb-2">
              {tr("Nova contrasenya", "Nueva contraseña")}
            </h1>
            <p className="text-sm text-slate-400 mb-7">
              {tr(
                "Tria una contrasenya segura de mínim 8 caràcters.",
                "Elige una contraseña segura de al menos 8 caracteres."
              )}
            </p>

            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                  {tr("Contrasenya nova", "Contraseña nueva")}
                </label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    data-testid="reset-password-input"
                    type={show ? "text" : "password"}
                    value={pwd}
                    onChange={(e) => setPwd(e.target.value)}
                    required
                    minLength={8}
                    autoFocus
                    className="bg-[#05070D] border-white/10 pl-9 pr-10 h-11"
                    placeholder="••••••••" />
                  <button type="button" onClick={() => setShow(s => !s)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                          data-testid="reset-toggle-visibility">
                    {show ? <EyeOff size={14} /> : <Eye size={14} />}
                  </button>
                </div>
              </div>
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                  {tr("Repeteix la contrasenya", "Repite la contraseña")}
                </label>
                <div className="relative">
                  <Lock size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
                  <Input
                    data-testid="reset-password-confirm"
                    type={show ? "text" : "password"}
                    value={pwd2}
                    onChange={(e) => setPwd2(e.target.value)}
                    required
                    minLength={8}
                    className="bg-[#05070D] border-white/10 pl-9 h-11"
                    placeholder="••••••••" />
                </div>
                {pwd2 && pwd2 !== pwd && (
                  <p className="text-xs text-rose-300 mt-1.5"
                     data-testid="reset-mismatch-error">
                    {tr("Les contrasenyes no coincideixen", "Las contraseñas no coinciden")}
                  </p>
                )}
              </div>
              {needs2fa && (
                <div data-testid="reset-totp-section"
                     className="rounded-lg bg-purple-500/5 border border-purple-500/30 p-3 space-y-2">
                  <div className="flex items-center gap-2 text-xs text-purple-200">
                    <KeyRound size={12} />
                    <span className="font-semibold uppercase tracking-wider">
                      {tr("Verificació en dos passos", "Verificación en dos pasos")}
                    </span>
                  </div>
                  <p className="text-xs text-slate-400 leading-relaxed">
                    {tr(
                      "Aquest compte té 2FA actiu. Obre la teva app d'autenticador i introdueix el codi de 6 dígits.",
                      "Esta cuenta tiene 2FA activo. Abre tu app de autenticador e introduce el código de 6 dígitos."
                    )}
                  </p>
                  <div className="relative">
                    <KeyRound size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-purple-400" />
                    <Input
                      data-testid="reset-totp-input"
                      value={totp}
                      onChange={(e) => setTotp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      maxLength={6}
                      inputMode="numeric"
                      autoFocus
                      className="bg-[#05070D] border-purple-500/30 pl-9 h-11 tracking-[0.4em] font-mono text-center"
                      placeholder="000000" />
                  </div>
                </div>
              )}
              <Button
                type="submit"
                disabled={busy || !valid}
                data-testid="reset-submit"
                className="w-full h-11 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.3)] disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
                {tr("Actualitza la contrasenya", "Actualizar contraseña")}
              </Button>
            </form>
          </>
        )}
      </div>
      <Toaster position="top-right" theme="dark" richColors />
    </div>
  );
}
